import {SyntheticEvent, useEffect, useMemo, useRef, useState} from 'react'
import moment from 'moment'
import {Field, FormikProps} from 'formik'
import {clientTimeZoneOffset, FLOW_TYPE, Languages, DEFAULT_WORKER, locale} from 'app/utils/common/constants'
//Components
import AccordionItem from '../../components/AccordionItem'
import {DateTimePickerInline, FieldDateTimePickerInline} from 'shared/DateTime/index'
//Type
import {IForm} from '../../components/type'
import {ticketApi} from 'app/modules/tickets/redux/TicketApi'
import {
  computeCalendarDelta,
  computeCalendarLimitDays,
  loadPackageCalendarPageV2,
  loadPackageCalendarV2,
  mergeCalendarDays,
} from 'app/modules/tickets/redux/PackageAvailabilityV2'
import {
  DatePackageAvailable,
  PackageServiceInfoRequest,
  RouteOptimizationCfg,
  Worker,
} from 'app/modules/tickets/models/OrderDrawerModel'
import {toast} from 'react-toastify'
import {ToastDefaultConfig} from 'setup/toast/ToastConfig'
import {useTranslation} from 'react-i18next'
import {TimeSlot, TimeSlotPackage} from 'app/utils/common/models'
import {
  applySlotDisplayWindow,
  getDateSelectedSingleVisitPerWeek,
  highlightDateSeveralVistPackage,
  pickRouteOptimizationCfg,
  resolveCreateOrderSlotDisplayContext,
  shouldShowDeliveryWindow,
} from 'app/utils/helpers/OrderDrawerHelpers'
import DateTimeVisit from './DateTimeVisit'
import {TSelectedPackageItem} from './type'
import {classNameFunc} from 'app/utils/helpers/components/Form/utils'
import {useAppSelector} from 'setup/redux/Hooks'
import './style.scss'

const DateTimePackage = (props: {
  formProps: FormikProps<IForm>
  language: string
  index?: number,
  customerLat?: number,
  customerLng?: number,
}) => {
  const {
    formProps: {values, validateField, setValues, setFieldTouched},
    index = 0,
    language,
  } = props
  const {t} = useTranslation()
  const isWatanCompany = useAppSelector((s) => s.auth.user?.isWatanCompany)
  const flowType = values.flow_type

  const number_visit = values.numberOfVisitPerWeek || 0
  const number_week = values.numberOfWeeks || 0
  const isMulti = flowType && flowType === FLOW_TYPE.PACKAGE && number_visit > 1 && number_week >= 1

  const touched = props.formProps?.touched['date_time']
  let _error = props.formProps?.errors['date_time']

  if (Array.isArray(_error)) {
    _error = _error.filter((e) => e !== undefined && e !== null)[0]
  }

  let [error, params = ''] = ![null, undefined, ''].includes(_error as any)
    ? String(typeof _error == 'object' ? Object.entries(_error).map((e) => e[1])[0] : _error).split(
        '-'
      )
    : [_error, '']
  error = ![null, undefined, ''].includes(error as any)
    ? t(error as string).format(params.split(';'))
    : error

  const isInvalid = touched !== false && touched !== undefined && error !== undefined

  const valuesRef = useRef(values)
  valuesRef.current = values

  const slotDisplayContext = useMemo(() => resolveCreateOrderSlotDisplayContext(values), [values])
  const isDeliveryTimeDisplay = shouldShowDeliveryWindow(
    slotDisplayContext.routeOptimizationCfg,
    values.isShowDeliveryTime,
    slotDisplayContext.visitReminder,
  )
  const isWorkerSpecific = Boolean(
    values.worker && values.worker !== DEFAULT_WORKER
  )
  const isWorkerCalendarFetchDone = Boolean(
    isWorkerSpecific &&
      values.loadingType !== 'worker' &&
      values.workerDayAvailableWorkerId === values.worker
  )
  const isWorkerCalendarPending = Boolean(
    isWatanCompany && isWorkerSpecific && !isWorkerCalendarFetchDone
  )

  const [state, setState] = useState<{
    minDate: Date
    isLoadingPicker: boolean
    dateHighlighted: TSelectedPackageItem[]
    activeDates: DatePackageAvailable[]
    excludeDates: DatePackageAvailable[]
    selected?: TSelectedPackageItem[]
    cursor?: Date
  }>({
    minDate: new Date(),
    isLoadingPicker: false,
    dateHighlighted: [],
    activeDates: [],
    excludeDates: [],
    cursor: new Date(),
    selected: [],
  })

  const parseDateCheck = (raw: any): Date => {
    if (raw instanceof Date) return raw
    // Accept common formats from API
    const m = moment(raw, ['M/D/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD', 'YYYY/MM/DD', moment.ISO_8601], true)
    return m.isValid() ? m.toDate() : new Date(raw)
  }

  const isSameCalendarDay = (raw: Date | string, target: Date): boolean =>
    moment(parseDateCheck(raw)).isSame(moment(target), 'day')

  const normalizeDayAvailable = (days: DatePackageAvailable[] = []): DatePackageAvailable[] => {
    return [...(days || [])]
      .map((d) => ({...d, dateCheck: parseDateCheck((d as any).dateCheck)}))
      .sort((a, b) => parseDateCheck(a.dateCheck).getTime() - parseDateCheck(b.dateCheck).getTime())
  }

  const resolveCalendarGridDaySource = (formValues: IForm): DatePackageAvailable[] => {
    const baseDays =
      formValues.preSelectSPBaseDayAvailable || formValues.preSelectSP?.dayAvailable || []

    if (formValues.worker && formValues.worker !== DEFAULT_WORKER) {
      const workerCalendarReady =
        formValues.loadingType !== 'worker' &&
        formValues.workerDayAvailable &&
        formValues.workerDayAvailable.length > 0 &&
        formValues.workerDayAvailableWorkerId === formValues.worker

      if (workerCalendarReady) return formValues.workerDayAvailable!

      const otherWorker = formValues.worker_available?.find(
        (s) => 'workerId' in s && s.workerId === formValues.worker
      ) as Worker | undefined
      if (otherWorker?.listAvailableDay?.length) return otherWorker.listAvailableDay
    }

    return baseDays
  }

  /** Base-on + cluster route optimization: single SP from preSelectProvider (package load). */
  const resolveCalendarBusIds = (formValues: IForm): string[] | undefined => {
    if (
      formValues.hasClusterRouteOptimization &&
      formValues.worker === DEFAULT_WORKER &&
      formValues.preSelectSP?.spId
    ) {
      return [formValues.preSelectSP.spId]
    }
    return formValues.spIds?.length ? formValues.spIds : undefined
  }

  const calendarResetContextRef = useRef({
    worker: values.worker,
    package: values.package,
  })

  const setDateClassName = (
    ...params: Parameters<
      React.ComponentProps<typeof FieldDateTimePickerInline>['customDateClassName']
    >
  ): string | null => {
    if (number_visit === 1 && number_week === 1) return null
    const [date] = params
    const isVertical = number_visit === 1 && number_week > 1
    if (state.selected) {
      if (isVertical) {
        const lastDateSelected = state.selected[state.selected.length - 1]
        if (lastDateSelected && date.isSame(new Date(lastDateSelected.date))) {
          return 'react-datepicker__day--highlighted--last--vertical '
        }
      }
      const transform_date = (
        Array.isArray(state.selected) ? state.selected : [state.selected]
      ).filter((el) => !el.related)
      let nametext = ''
      const idx = transform_date.findIndex((item: TSelectedPackageItem) =>
        new Date(item.date).isSame(date)
      )
      if (idx === -1) {
        return null
      } else {
        if (isVertical) {
          nametext = 'react-datepicker__day--selected--vertical '
        } else {
          if (transform_date.length > 1) {
            if (idx === 0) {
              nametext =
                language === Languages.ar
                  ? 'react-datepicker__day--selected--first--rtl '
                  : 'react-datepicker__day--selected--first '
            }
            if (idx === transform_date.length - 1) {
              nametext =
                language === Languages.ar
                  ? 'react-datepicker__day--selected--last--rtl '
                  : 'react-datepicker__day--selected--last '
            }
          }
        }

        if (
          state.cursor &&
          new Date(transform_date[idx].date).isSame(state.cursor) &&
          transform_date[idx].time === undefined
        ) {
          nametext +=
            transform_date.length === 1
              ? 'react-datepicker__day--showing react-datepicker__day--selected--one '
              : 'react-datepicker__day--showing react-datepicker__day--selected '
        } else {
          nametext +=
            transform_date.length === 1
              ? 'react-datepicker__day--selected--one '
              : 'react-datepicker__day--selected '
        }
      }

      return nametext
    }
    return null
  }

  // Memoize excludeDates conversion to avoid re-computing on every render
  const excludeDatesConverted = useMemo(() => {
    if (state.excludeDates.length === 0) return []
    const excluded = state.excludeDates.map(day => {
      // Convert DatePackageAvailable to Date and normalize
      const date = day.dateCheck instanceof Date 
        ? new Date(day.dateCheck)
        : moment(day.dateCheck, 'M/D/YYYY').toDate()
      // Normalize to midnight UTC to ensure accurate comparison
      const normalized = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
      return normalized
    })
    return excluded
  }, [state.excludeDates])

  useEffect(() => {
    const prevContext = calendarResetContextRef.current
    const workerChanged = prevContext.worker !== values.worker
    const packageChanged = prevContext.package !== values.package
    calendarResetContextRef.current = {
      worker: values.worker,
      package: values.package,
    }

    if (workerChanged || packageChanged) {
      setValues((s) => ({
        ...s,
        date_time: undefined,
        selectedDateRouteOptimizationCfg: undefined,
        selectedDateVisitReminder: undefined,
      }))
      setFieldTouched('date_time', false)
    }

    if (!values.preSelectSP) return

    if (isWorkerCalendarPending) {
      setState((prev) => ({
        ...prev,
        activeDates: [],
        excludeDates: [],
        dateHighlighted: [],
        cursor: undefined,
        selected: [],
        isLoadingPicker: false,
      }))
      return
    }

    const normalizedDaySource = normalizeDayAvailable(resolveCalendarGridDaySource(values))
    const workerCalendarReady =
      isWorkerSpecific &&
      values.loadingType !== 'worker' &&
      values.workerDayAvailableWorkerId === values.worker &&
      !!values.workerDayAvailable?.length

    setState((prev) => {
      const activeDates = workerCalendarReady
        ? normalizeDayAvailable(values.workerDayAvailable!)
        : workerChanged || packageChanged
          ? normalizedDaySource
          : mergeCalendarDays(normalizedDaySource, prev.activeDates)

      const firstAvailable = normalizedDaySource.find((d) => d.isAvailable)
      const availableDate = firstAvailable
        ? parseDateCheck(firstAvailable.dateCheck)
        : new Date().addDays(1)
      const unAvailiableDates = activeDates.filter((d) => !d.isAvailable)

      if (workerChanged || packageChanged) {
        return {
          ...prev,
          minDate: availableDate,
          excludeDates: unAvailiableDates,
          dateHighlighted: [],
          activeDates,
          cursor: undefined,
          selected: [],
          isLoadingPicker: false,
        }
      }

      return {
        ...prev,
        minDate: availableDate,
        excludeDates: unAvailiableDates,
        activeDates,
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    values.preSelectSPBaseDayAvailable,
    values.workerDayAvailable,
    values.workerDayAvailableWorkerId,
    values.preSelectSP?.spId,
    values.preSelectSP?.companyId,
    values.worker,
    values.package,
    values.loadingType,
    isWorkerCalendarPending,
    values.worker_available,
    values.main_service,
    values.map,
    values.phone,
  ])

  const getDaysAvaliable = async (date: Date, packageInfo: PackageServiceInfoRequest) => {
    try {
      // V2: fetch the calendar with selectedDate so the matching day comes back with its time slots.
      // Match the currently-loaded paginated window so the calendar grid doesn't shrink on a date click.
      const {legacy: data, calendar} = await loadPackageCalendarV2(
        {
          packageWorkflowId: packageInfo.packageWorkflowId || 0,
          countryId: packageInfo.countryId || 0,
          packageId: packageInfo.selectedPackage?.id || 0,
          majorId: packageInfo.majorId,
          clientTimeOffset: packageInfo.clientTimeOffset || -clientTimeZoneOffset,
          companyId: packageInfo.companyId,
          customerId: packageInfo.customerId,
          customerPhone: packageInfo.customerPhone,
          customerLat: packageInfo.customerLat,
          customerLng: packageInfo.customerLng,
          spIds: packageInfo.spIds,
          zoneIds: packageInfo.zoneIds,
          workerId: packageInfo.workerId,
          numbOfSelectWorker: packageInfo.numbOfSelectWorker,
          selectedDate: packageInfo.selectedDate,
          limitDays: values.calendarLoadedUntilDay ?? computeCalendarLimitDays(new Date(), new Date()),
        },
        values.packageDetailsV2 ?? null
      )

      const preSelectSP = data.packageInfo[0]?.preSelectSP
      const availiable = preSelectSP?.dayAvailable || []
      const routeOptimizationCfg =
        pickRouteOptimizationCfg(calendar?.preSelectProvider) ??
        pickRouteOptimizationCfg(preSelectSP) ??
        pickRouteOptimizationCfg(data.packageInfo[0]) ??
        null

      return {
        dayAvailable: normalizeDayAvailable(availiable),
        packageShift: data.packageInfo[0]?.shiftType || 0,
        routeOptimizationCfg,
        visitReminder:
          calendar?.preSelectProvider?.visitReminder ??
          preSelectSP?.visitReminder ??
          '',
      }
    } catch (error) {
      console.log(error)
      toast.error(`${t('ERROR_MESSAGE.DATE_TIME_ERROR.UNEXPECTED_ERROR')}`, ToastDefaultConfig())
      return
    }
  }

  // Lazy-expand the calendar window when the user navigates to a month past the last loaded day.
  // Sends a delta request (`startDay` = current loaded count, `limitDays` = days needed for the
  // displayed month grid) and merges the new days into the existing availability state.
  const onChangeMonth = async (displayedMonth: Date) => {
    if (!values.preSelectSP) return
    const delta = computeCalendarDelta(displayedMonth, values.calendarLoadedUntilDay)
    if (!delta) return // window already covers this month

    const packageWorkflowId = values.main_service?.data?.packageWorkFlowId || 0
    const countryId = values.location?.countryId || 0
    const packageId = Number(values.package) || 0
    const majorId = values.main_service?.id || 0
    if (!packageId || !majorId || !packageWorkflowId) return

    setState(s => ({...s, isLoadingPicker: true}))
    try {
      const {days} = await loadPackageCalendarPageV2({
        packageWorkflowId,
        countryId,
        packageId,
        majorId,
        clientTimeOffset: -clientTimeZoneOffset,
        companyId: values.preSelectSP?.companyId || '',
        customerId: values.customer_id,
        customerPhone: values.phone,
        customerLat: props.customerLat,
        customerLng: props.customerLng,
        spIds: resolveCalendarBusIds(values),
        zoneIds: values.zoneIds,
        workerId:
          values.worker && values.worker !== DEFAULT_WORKER ? values.worker : undefined,
        numbOfSelectWorker:
          values.workerConfig?.isApply &&
          !values.workerConfig.isRequireMatchWorkerNumber &&
          values.number_of_workers > 1
            ? values.number_of_workers
            : undefined,
        startDay: delta.startDay,
        limitDays: delta.limitDays,
      })

      const newDays = normalizeDayAvailable(days)
      const mergedBase = mergeCalendarDays(values.preSelectSPBaseDayAvailable, newDays)
      const mergedWorker = values.workerDayAvailable
        ? mergeCalendarDays(values.workerDayAvailable, newDays)
        : undefined

      setValues(s => ({
        ...s,
        preSelectSP: s.preSelectSP
          ? {...s.preSelectSP, dayAvailable: mergeCalendarDays(s.preSelectSP.dayAvailable, newDays)}
          : s.preSelectSP,
        preSelectSPBaseDayAvailable: mergedBase,
        workerDayAvailable: mergedWorker,
        calendarLoadedUntilDay: delta.requiredDays,
      }))

      setState(s => ({
        ...s,
        isLoadingPicker: false,
        activeDates: mergeCalendarDays(s.activeDates, newDays),
        excludeDates: mergeCalendarDays(
          s.excludeDates,
          newDays.filter(d => !d.isAvailable)
        ),
      }))
    } catch (e) {
      console.log(e)
      setState(s => ({...s, isLoadingPicker: false}))
    }
  }

  const onChangeDate = async (date?: Date | null, event?: SyntheticEvent<any> | undefined) => {
    if (date !== null && date !== undefined) {

      void onChangeMonth(date)
      let packageRequest = {
        packageWorkflowId: values.main_service?.data?.packageWorkFlowId || 0,
        countryId: values.location?.countryId,
        isGetAll: true,
        clientTimeOffset: -clientTimeZoneOffset,
        customerId: values.customer_id,
        companyId: values.preSelectSP?.companyId || '',
        majorId: values.main_service?.id || 0,
        selectedDate: moment(date).locale(locale.EN).format('YYYY/MM/DD'),
        selectedPackage: {
          id: Number(values.package),
          spid: values.preSelectSP?.spId || '',
          HasUnratedWorker: false,
          FavWorkerIds: [],
        },
        spIds: resolveCalendarBusIds(values),
        workerId: values.worker && values.worker !== DEFAULT_WORKER ? values.worker : undefined,
        zoneIds: values.zoneIds,
        customerLat: props.customerLat,
        customerLng: props.customerLng,
        customerPhone: values.phone,
      } as PackageServiceInfoRequest

      if (
        values.workerConfig?.isApply &&
        !values.workerConfig.isRequireMatchWorkerNumber &&
        values.number_of_workers > 1
      ) {
        packageRequest.numbOfSelectWorker = values.number_of_workers
      }
      let cursor = state.cursor !== undefined && date.isSame(state.cursor) ? undefined : date

      // Handle remove date when click date again
      if (state.selected !== undefined && state.selected.length !== 0) {
        let current = [...state.selected]
        const idx = current.findIndex(
          (el) => !el.related && new Date(el.date).isSame(new Date(date))
        )

        if (idx > -1) {
          current = [...state.selected].filter(
            (el) =>
              !(
                (el.related && new Date(el.related).isSame(new Date(date))) ||
                new Date(el.date).isSame(new Date(date))
              )
          )
          let highlighted: TSelectedPackageItem[] = []
          let activate = [...state.activeDates]

          if (number_visit > 1 && number_week >= 1) {
            if (current.length === 0) {
              activate = []
            }

            const mainDate = current.filter((e) => !e.related)
            if (mainDate.length !== 0) {
              const selectedStart = mainDate[0].date
              const selectedEnd = mainDate[mainDate.length - 1].date
              highlighted = highlightDateSeveralVistPackage(
                new Date(selectedStart),
                new Date(selectedEnd)
              )
            }
          }
          setState({
            ...state,
            selected: current,
            activeDates: activate,
            dateHighlighted: highlighted,
            cursor: current[0]?.date || undefined,
          })
          changeFieldsValue(current)
          return
        }
      }

      //
      if (number_visit > 1) {
        const totalVisit = number_visit * number_week
        if (state.selected?.length === totalVisit) {
          const mainSelected = state.selected.filter((e) => !e.related)
          const idx = mainSelected.findIndex(
            (e) => cursor && new Date(e.date).isSame(new Date(cursor))
          )
          if (idx === -1) {
            toast.error(
              `${t('ERROR_MESSAGE.DATE_TIME_ERROR.RANGE_LIMIT_NUMBER_DATE')}`,
              ToastDefaultConfig()
            )
            return
          }
        }
        if (number_week >= 1) {
          const pickedDatePerWeek = state.selected?.filter((s) => !s.related) || []
          if (pickedDatePerWeek.length > number_visit) {
            toast.error(
              `${t('ERROR_MESSAGE.DATE_TIME_ERROR.RANGE_LIMIT_NUMBER_DATE')}`,
              ToastDefaultConfig()
            )
            return
          }
        }
      }
      setState({...state, isLoadingPicker: true, cursor})

      let respData:
        | {
            dayAvailable: DatePackageAvailable[]
            packageShift: number
            routeOptimizationCfg?: RouteOptimizationCfg | null
            visitReminder?: string
          }
        | undefined = {
        dayAvailable: [],
        packageShift: 0,
      }

      let AvaliableDate
      let arrayDateActive = [...state.activeDates]
      let arrayInActiveDate = [...state.excludeDates]

      // Always call /get-package-available when user picks a date on the calendar.
      // (Previously this was gated by activeDates.length, but activeDates is now pre-filled to show availability,
      // so gating prevents the API call and breaks time-slot fetching.)
      respData = await getDaysAvaliable(date as any, packageRequest as PackageServiceInfoRequest)

      if (!respData || !respData.dayAvailable.length) {
        setState({...state, isLoadingPicker: false})
        toast.error(`${t('ERROR_MESSAGE.DATE_TIME_ERROR.NOT_AVAILABLE')}`, ToastDefaultConfig())
        return
      }

      const datePickResponse = respData
      const gridBase = normalizeDayAvailable(resolveCalendarGridDaySource(valuesRef.current))

      // selectedDate response returns only the picked day with time slots — merge into the full grid.
      arrayDateActive = mergeCalendarDays(
        mergeCalendarDays(gridBase, state.activeDates),
        datePickResponse.dayAvailable
      )
      arrayInActiveDate = arrayDateActive.filter((d) => !d.isAvailable)
      AvaliableDate = arrayDateActive.find(
        (e) => isSameCalendarDay(e.dateCheck, date) && e.isAvailable === true
      )

      if (AvaliableDate) {
        const currentValues = valuesRef.current
        const {routeOptimizationCfg, visitReminder} = resolveCreateOrderSlotDisplayContext(
          currentValues,
          {
            routeOptimizationCfg: datePickResponse.routeOptimizationCfg,
            visitReminder: datePickResponse.visitReminder,
          },
        )

        if (AvaliableDate.listTimes?.length) {
          AvaliableDate.listTimes = applySlotDisplayWindow(
            AvaliableDate.listTimes,
            routeOptimizationCfg,
            visitReminder,
            currentValues.isShowDeliveryTime,
          )
        }

        // Persist date-scoped slot display — do not touch preSelectSP (avoids availability useEffect reset).
        if (currentValues.worker === DEFAULT_WORKER) {
          setValues((s) => ({
            ...s,
            selectedDateRouteOptimizationCfg: datePickResponse.routeOptimizationCfg ?? null,
            selectedDateVisitReminder: datePickResponse.visitReminder ?? '',
          }))
        } else if (currentValues.worker && currentValues.worker !== DEFAULT_WORKER) {
          setValues((s) => ({
            ...s,
            selectedWorkerRouteOptimizationCfg: datePickResponse.routeOptimizationCfg ?? null,
            selectedDateRouteOptimizationCfg: undefined,
            selectedDateVisitReminder: undefined,
            ...(datePickResponse.visitReminder
              ? {selectedWorkerVisitReminder: datePickResponse.visitReminder}
              : {selectedWorkerVisitReminder: undefined}),
          }))
        }
        if (!new Date(AvaliableDate.dateCheck).isSame(date)) {
          cursor = AvaliableDate.dateCheck

          arrayInActiveDate.push({
            dateCheck: new Date(date),
            isAvailable: false,
          } as DatePackageAvailable)
          toast.error(
            `${t('ERROR_MESSAGE.DATE_TIME_ERROR.NEAREST_AVALIABLE_DATE')} ${cursor}`,
            ToastDefaultConfig()
          )
        }

        const timeSlotTemp = AvaliableDate?.listTimes || []

        const timeSlot = timeSlotTemp.map((s, index) => ({...s, isSelected: false}))

        // PreSelect TimeSlot start
        let initTime: TimeSlotPackage | undefined
        const availiableTimeSlots = timeSlot.filter((el) => el.isAvailable)
        switch (
          respData.packageShift // Filter timeSlot allow packageShift
        ) {
          case 1: //Morning Shift  5am --> 4pm
            if (availiableTimeSlots.find((el) => el.start >= 300 && el.start < 960) !== undefined) {
              initTime = availiableTimeSlots.find((el) => el.start >= 300 && el.start < 960)
            }
            break
          case 2: // Night Shift 4pm --> 5am ( of the next day)
            if (availiableTimeSlots.find((el) => el.start < 300 || el.start >= 960) !== undefined) {
              initTime = availiableTimeSlots.find((el) => el.start < 300 || el.start >= 960)
            }
            break
          default: // All
            if (availiableTimeSlots.length === 1) {
              initTime = availiableTimeSlots[0]
            }
            break
        }
        if (initTime) {
          initTime.isSelected = true
        }
        // PreSelect TimeSlot end

        let selected = [] as typeof state.selected
        if (cursor) {
          if (!isMulti) {
            selected =
              number_visit === 1 && number_week > 1
                ? getDateSelectedSingleVisitPerWeek(
                    new Date(cursor),
                    [],
                    number_week,
                    timeSlot,
                    initTime
                  )
                : [{date: cursor, time: initTime, timeSlot: timeSlot, related: undefined}]
          } else {
            selected = [
              ...(state.selected || []),
              ...((state.selected || []).findIndex((e) => cursor && e.date.isSame(cursor)) < 0
                ? getDateSelectedSingleVisitPerWeek(
                    new Date(cursor),
                    state.selected || [],
                    number_week,
                    timeSlot,
                    initTime
                  )
                : []),
            ].sort((a, b) => a.date.getTime() - b.date.getTime())
          }
        }

        if (cursor !== null && cursor !== undefined) {
          if (number_visit === 1 && number_week > 1) {
            setState({
              ...state,
              cursor,
              selected,
              isLoadingPicker: false,
              dateHighlighted: getDateSelectedSingleVisitPerWeek(
                new Date(cursor),
                [],
                number_week,
                timeSlot,
                initTime
              ),
              activeDates: arrayDateActive,
              excludeDates: arrayInActiveDate,
            })
          } else if (number_visit > 1 && number_week >= 1) {
            let highlighted: TSelectedPackageItem[] = []
            if (cursor) {
              let start = cursor
              if (state.selected && state.selected.length !== 0) {
                const mainDate = state.selected.filter((e) => !e.related)
                const selectedStart = mainDate[0].date
                const selectedEnd = mainDate[mainDate.length - 1].date
                if (new Date(cursor).getTime() >= selectedEnd.getTime()) {
                  start = mainDate[0].date
                  highlighted = highlightDateSeveralVistPackage(new Date(start), new Date(cursor))
                } else if (
                  new Date(cursor).getTime() > selectedStart.getTime() &&
                  new Date(cursor).getTime() < selectedEnd.getTime()
                ) {
                  highlighted = highlightDateSeveralVistPackage(
                    new Date(selectedStart),
                    new Date(selectedEnd)
                  )
                } else {
                  highlighted = highlightDateSeveralVistPackage(
                    new Date(cursor),
                    new Date(selectedEnd)
                  )
                }

                if (mainDate.findIndex((e) => e.time === undefined) > -1) {
                  cursor = mainDate[mainDate.findIndex((e) => e.time === undefined)].date
                }
              }
            }
            setState({
              ...state,
              cursor,
              selected,
              isLoadingPicker: false,
              dateHighlighted: highlighted,
              activeDates: arrayDateActive,
              excludeDates: arrayInActiveDate,
            })
          } else {
            setState({
              ...state,
              cursor,
              selected,
              activeDates: arrayDateActive,
              excludeDates: arrayInActiveDate,
              isLoadingPicker: false,
            })
          }
        }

        changeFieldsValue(selected)
      } else {
        setState({...state, isLoadingPicker: false})
        toast.error(`${t('ERROR_MESSAGE.DATE_TIME_ERROR.UNEXPECTED_ERROR')}`, ToastDefaultConfig())
        return
      }
    }
  }

  const onSelectTimeSlot = (d: Date, e: TimeSlotPackage) => {
    if (!e.isAvailable || state.cursor === undefined) return

    const selected = [...(state.selected || [])]
    const find = selected.find((e) => new Date(e.date).isSame(d))
    if (find == null) return
    find.timeSlot?.forEach((slot) =>
      slot.start === e.start ? (slot.isSelected = true) : (slot.isSelected = false)
    )

    if (number_visit === 1 && number_week > 1) {
      selected.forEach((item) => {
        item.time = e
      })
    } else if (number_visit > 1 && number_week >= 1) {
      selected.forEach((item) => {
        if (
          (item.related && item.related.isSame(new Date(d))) ||
          (!item.related && item.date.isSame(new Date(d)))
        ) {
          item.time = e
        }
      })
    } else {
      find.time = e
    }

    setState({...state, selected})
    changeFieldsValue(selected)
  }

  const onClearSelected = (date: Date, time?: TimeSlotPackage) => {
    let selected = [...(state.selected || [])].filter((e) =>
      time === undefined ? (!e.related ? !e.date.isSame(date) : !e.related?.isSame(date)) : true
    )
    if (time !== undefined) {
      if (number_visit === 1 && number_week > 1) {
        const find = selected.filter((e) => e.date.isSame(date) || e.related?.isSame(date)) || []
        if (find) {
          find.forEach((item) =>
            item.timeSlot?.forEach((slot) =>
              slot.start === time.start ? (slot.isSelected = false) : slot.isSelected
            )
          )
        }
        selected = selected.map((s) => ({
          ...s,
          time: undefined,
        }))
      } else if (number_visit > 1 && number_week >= 1) {
        const find = selected.filter((e) => e.date.isSame(date) || e.related?.isSame(date)) || []
        if (find) {
          find.forEach((item) =>
            item.timeSlot?.forEach((slot) =>
              slot.start === time.start ? (slot.isSelected = false) : slot.isSelected
            )
          )
        }
        selected = selected.map((s) =>
          s.date.isSame(date) || s.related?.isSame(date)
            ? {
                ...s,
                time: undefined,
              }
            : s
        )
      } else {
        const find = selected.find((e) => e.date.isSame(date))
        if (find) find.time = undefined
        find?.timeSlot?.forEach((slot) =>
          slot.start === time.start ? (slot.isSelected = false) : slot.isSelected
        )
      }
    }
    let highlighted = [...state.dateHighlighted]
    let actives = [...state.activeDates]
    let newCursor = state.cursor
    if (number_visit > 1 && number_week >= 1) {
      if (selected.length === 0) {
        highlighted = []
        actives = []
        newCursor = undefined
      } else {
        const mainSelected = selected.filter((e) => !e.related)
        newCursor = mainSelected[mainSelected.length - 1].date
        highlighted = highlightDateSeveralVistPackage(
          mainSelected[0].date,
          mainSelected[mainSelected.length - 1].date
        )
      }
    } else if (number_visit === 1 && number_week > 1) {
      if (time === undefined) {
        highlighted = []
      }
    } else {
      highlighted = []
    }
    if (number_visit > 1 && number_week >= 1) {
      setState({
        ...state,
        dateHighlighted: highlighted,
        activeDates: actives,
        selected,
        ...(time !== undefined ? {cursor: date} : {cursor: newCursor}),
      })
    } else {
      setState({
        ...state,
        dateHighlighted: highlighted,
        selected,
        ...(time === undefined && {cursor: selected[0]?.date}),
      })
    }
    changeFieldsValue(selected)
  }

  const changeFieldsValue = (selected: typeof state.selected) => {
    setValues((s) => ({
      ...s,
      date_time:
        isMulti === true || (number_visit === 1 && number_week > 1)
          ? (selected as TSelectedPackageItem[])
          : (selected?.[0] as {
              date?: Date
              time?: TimeSlot
              timeSlot?: TimeSlot[]
              customerVisitDuration?: number
            }),
      selected_visit: selected || ([] as TSelectedPackageItem[]),
    }))
    validateField('date_time')
  }

  // preSelectProvider.routeOptimizationCfg may arrive after worker calendar refetch — re-apply slot window.
  useEffect(() => {
    if (!state.selected?.length) return

    const {routeOptimizationCfg, visitReminder} = resolveCreateOrderSlotDisplayContext(values)
    let changed = false

    const selected = state.selected.map((item) => {
      if (!item.timeSlot?.length) return item

      const remappedSlots = applySlotDisplayWindow(
        item.timeSlot,
        routeOptimizationCfg,
        visitReminder,
        values.isShowDeliveryTime,
      )

      const slotsDiffer = remappedSlots.some(
        (slot, i) =>
          slot.visitReminderStart !== item.timeSlot![i]?.visitReminderStart ||
          slot.visitReminderEnd !== item.timeSlot![i]?.visitReminderEnd,
      )

      if (!slotsDiffer) return item

      changed = true
      const remappedTime =
        item.time
          ? remappedSlots.find(
              (s) => s.start === item.time!.start && s.end === item.time!.end,
            ) ?? item.time
          : item.time

      return { ...item, timeSlot: remappedSlots, time: remappedTime }
    })

    if (!changed) return

    setState((prev) => ({ ...prev, selected }))
    changeFieldsValue(selected)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slotDisplayContext.routeOptimizationCfg, slotDisplayContext.visitReminder, values.worker, values.selectedDateRouteOptimizationCfg])

  const timeSlotsOption =
    state.cursor !== undefined
      ? state.selected?.find((e) => new Date(e.date).isSame(state.cursor as any))?.timeSlot
      : undefined

  const selectDateRequired =
    number_visit === 1
      ? t('CREATE_ORDER_DRAWER.SECTION_3.NUMBER_SELECT_1_DATE')
      : number_visit === 2
      ? t('CREATE_ORDER_DRAWER.SECTION_3.NUMBER_SELECT_2_DATE')
      : t('CREATE_ORDER_DRAWER.SECTION_3.NUMBER_SELECT_MULTI_DATE', {num: number_visit})

  return (
    <AccordionItem id='date_time' title={`${index}. ${t('CREATE_ORDER_DRAWER.SECTION_3.TITLE')}`}>
      <p className='section-title'>{t('CREATE_ORDER_DRAWER.SECTION_3.SUB_TITLE')}</p>
      {!(values.worker === null || values.worker === undefined) &&
        number_visit >= 1 &&
        number_week >= 1 && <span>{selectDateRequired}</span>}
      <div className={classNameFunc(isInvalid && 'has-error')}>
        {isInvalid && <p className='text-danger invalid-feedback'>{error}</p>}
      </div>
      <div className='date-time-picker-inline-component'>
        <Field
          name='date_time'
          component={DateTimePickerInline}
          value={state.cursor}
          minDate={state.minDate}
          disableDate={
            values.worker === null ||
            values.worker === undefined ||
            isWorkerCalendarPending
          }
          customDateClassName={setDateClassName}
          onChange={onChangeDate}
          onMonthChange={onChangeMonth}
          isLoading={state.isLoadingPicker || isWorkerCalendarPending}
          highlightDates={isWorkerCalendarPending ? [] : state.dateHighlighted}
          activeDates={isWorkerCalendarPending ? [] : state.activeDates}
          excludeDates={excludeDatesConverted}
          filterDate={(date: Date) => {
            if (isWorkerCalendarPending) return false

            // Also use filterDate as a backup to ensure dates are disabled
            // Normalize date to midnight UTC for comparison
            const normalizedDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
            
            const isExcluded = state.excludeDates.some(day => {
              if (day.isAvailable) return false // Only exclude dates with isAvailable = false
              const dayDate = day.dateCheck instanceof Date 
                ? new Date(day.dateCheck)
                : moment(day.dateCheck, 'M/D/YYYY').toDate()
              const normalizedDayDate = new Date(Date.UTC(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate()))
              
              // Compare normalized dates by timestamp
              const isMatch = normalizedDate.getTime() === normalizedDayDate.getTime()
              return isMatch
            })
            
            return !isExcluded
          }}
          openToDate={state.minDate}
          language={language}
        />
        <DateTimeVisit
          isLoading={state.isLoadingPicker || isWorkerCalendarPending}
          isMulti={isMulti === true}
          isDeliveryTime={isDeliveryTimeDisplay}
          isEmpty={timeSlotsOption?.filter((e) => e.isAvailable).length === 0}
          showWarning={false}
          timeSlotsOption={timeSlotsOption || []}
          dateTimeOptions={state.selected || []}
          lang={language}
          onSelectTimeSlot={onSelectTimeSlot}
          onClearSelected={onClearSelected}
        />
      </div>

      <p>
        {number_visit && number_week && !(number_visit === 1 && number_week === 1) ? (
          <p className='py-2'>
            <span style={{color: 'red'}}>{t('CREATE_ORDER_DRAWER.SECTION_3.NOTE_TITLE')}</span>
            {number_week % 4 === 0
              ? number_week / 4 === 1
                ? `${t('CREATE_ORDER_DRAWER.SECTION_3.REPEAT_MONTH', {
                    frequency: (number_week / 4).toString(),
                  })}`
                : `${t('CREATE_ORDER_DRAWER.SECTION_3.REPEAT_MONTHS', {
                    frequency: (number_week / 4).toString(),
                  })}`
              : `${t('CREATE_ORDER_DRAWER.SECTION_3.REPEAT_WEEK', {
                  frequency: number_week.toString(),
                })}`}
          </p>
        ) : null}
      </p>
    </AccordionItem>
  )
}

export default DateTimePackage
